import { AiOutlineLoading3Quarters } from "react-icons/ai";
import {
  FaHome,
  FaRegCalendar,
  FaRegClock,
  FaRegCheckCircle,
} from "react-icons/fa";
import { HiOutlineFolderDownload } from "react-icons/hi";
import RichText from "../../components/RichText";
import { useEffect, useState, useContext } from "react";
import {
  useForm,
  SubmitHandler,
  Controller,
  FormProvider,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { API, graphqlOperation } from "aws-amplify";
import { updateProductLibBanner } from "../../graphql/mutations";
import { UpdateProductLibBannerInput, ProductLibBanner } from "../../API";
import Select from "react-select";
import { brandOptions, bannerStatus, BrandsFN } from "../../constants/enum";
import FileUploaderInput from "../../components/FileUploaderInput";
import { Storage } from "aws-amplify";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import UserContext from "../../contexts/UserContext";
import BannerContext from "../../contexts/BannerContext";
import { RiDeleteBin5Line } from "react-icons/ri";
import ModalConfirm from "../../components/ModalConfirmNew";

const prefixS3 = "files";
const subfixS3 = "banners";

interface IFormInputs {
  inputPartImg: string;
  inputBrand: { label: string; value: string };
  inputTextTitle: string;
  inputTextDescription: string;
  inputStartDate: string;
  inputStartTime: string;
  inputEndDate?: string;
  inputEndTime?: string;
}

function BannerUpdate() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const userInfo: any = user;
  const { banner } = useContext(BannerContext) as {
    banner: ProductLibBanner | null;
  };

  const bannerId = banner?.id || "";
  const brandTypeNone = { label: "Neo Commu", value: "NEOCOMMU" };
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [isUpload, setIsUpload] = useState<boolean>(false);
  const [textDescription, setTextDescription] = useState<string>(
    banner?.description || ""
  );
  const [textTitle, setTextTitle] = useState<string>(banner?.name || "");
  const titleMaxLength = 90;
  const [submitStatus, setSubmitStatus] = useState<string>(
    banner?.status || bannerStatus.PUBLISHED
  );
  const [imagePreview, setImagePreview] = useState<any>([]);
  const [imageUploadDescription, setImageUploadDescription] = useState<File>(
    banner?.mimeType ? ({ type: banner?.mimeType } as any) : null
  );
  const [textStartDate, setTextStartDate] = useState<Date | null>(
    banner?.periodStart ? new Date(banner?.periodStart) : null
  );
  const [textStartTime, setTextStartTime] = useState<Date | null>(
    banner?.periodStart ? new Date(banner?.periodStart) : null
  );
  const [textEndDate, setTextEndDate] = useState<Date | null>(
    banner?.periodEnd ? new Date(banner?.periodEnd) : null
  );
  const [textEndTime, setTextEndTime] = useState<Date | null>(
    banner?.periodEnd ? new Date(banner?.periodEnd) : null
  );

  // disable submit button when image is uploaded
  useEffect(() => {
    if (imagePreview.length > 0) {
      setIsUpload(false);
    }
  }, [imagePreview]);
  useEffect(() => {
    if (imageUploadDescription) {
      setIsUpload(true);
    }
  }, [imageUploadDescription]);

  const [showConfirmRemove, setShowConfirmRemove] = useState<boolean>(false);
  const [removeImage, setRemoveImage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showSaveSubmit, setShowSaveSubmit] = useState<boolean>(false);
  const [showSaveDraft, setShowSaveDraft] = useState<boolean>(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState<boolean>(false);

  const BannerSchema: any = yup.object({
    inputPartImg: yup.string().required("Cover Banner is required"),
    inputBrand: yup.object().required("Brand is required"),
    inputTextTitle: yup
      .string()
      .max(titleMaxLength)
      .required("Title is required"),
    inputTextDescription: yup
      .string()
      .required("Description is required")
      .notOneOf(["<p><br></p>"], "Description is required"),
    inputStartDate: yup.string().required("Start Date is required"),
    inputStartTime: yup.string().required("Start Time is required"),
    inputEndDate: yup.string().nullable(),
    inputEndTime: yup.string().nullable(),
  });

  const methods = useForm<IFormInputs>({
    defaultValues: {
      inputPartImg: banner?.path || "",
      inputBrand:
        banner?.brand && banner?.brand !== brandTypeNone.value
          ? {
              label: BrandsFN[banner.brand as keyof typeof BrandsFN],
              value: banner.brand,
            }
          : brandTypeNone,
      inputTextTitle: banner?.name || "",
      inputTextDescription: banner?.description || "",
      inputStartDate: banner?.periodStart
        ? format(new Date(banner.periodStart), "dd/MM/yyyy")
        : "",
      inputStartTime: banner?.periodStart
        ? format(new Date(banner.periodStart), "HH:mm")
        : "",
      inputEndDate: banner?.periodEnd
        ? format(new Date(banner.periodEnd), "dd/MM/yyyy")
        : "",
      inputEndTime: banner?.periodEnd
        ? format(new Date(banner.periodEnd), "HH:mm")
        : "",
    },
    resolver: yupResolver(BannerSchema),
  });

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = methods;

  const convertToISO = (dateString: string, timeString: string): string => {
    const tempDate =
      dateString && isSpecificDateFormat(dateString)
        ? format(new Date(dateString), "dd/MM/yyyy")
        : dateString;
    const tempTime =
      timeString && isSpecificDateFormat(timeString)
        ? format(new Date(timeString), "HH:mm")
        : timeString;
    const [day, month, year] = tempDate.split("/").map(Number);
    const [hours, minutes] = tempTime.split(":").map(Number);
    const date = new Date(year, month - 1, day, hours, minutes);
    return date.toISOString();
  };

  const isSpecificDateFormat = (dateString: string): boolean => {
    const specificDateFormatRegex =
      /^[A-Za-z \u0E00-\u0E7F]{3} [A-Za-z \u0E00-\u0E7F]{3} \d{2} \d{4} \d{2}:\d{2}:\d{2} GMT[+-]\d{4} \([A-Za-z \u0E00-\u0E7F]+\)$/;
    return specificDateFormatRegex.test(dateString);
  };

  const onSubmitForm: SubmitHandler<IFormInputs> = async (dataForm) => {
    setIsSubmit(true);
    let partImg = "";
    if (imagePreview.length > 0) {
      partImg = imagePreview[0].value.key.split("/").slice(1).join("/");
    }

    const tempStartDate = isSpecificDateFormat(dataForm.inputStartDate)
      ? dataForm.inputStartDate
      : convertToISO(dataForm.inputStartDate, dataForm.inputStartTime);
    const tempStartTime = isSpecificDateFormat(dataForm.inputStartTime)
      ? dataForm.inputStartTime
      : convertToISO(dataForm.inputStartDate, dataForm.inputStartTime);

    const periodStart = new Date(
      `${format(new Date(tempStartDate), "yyyy-MM-dd")}T${format(
        new Date(tempStartTime),
        "HH:mm"
      )}`
    ).toISOString();

    let periodEnd = undefined;
    if (dataForm.inputEndDate && dataForm.inputEndTime) {
      const tempEndDate = isSpecificDateFormat(dataForm.inputEndDate)
        ? dataForm.inputEndDate
        : convertToISO(dataForm.inputEndDate, dataForm.inputEndTime);
      const tempEndTime = isSpecificDateFormat(dataForm.inputEndTime)
        ? dataForm.inputEndTime
        : convertToISO(dataForm.inputEndDate, dataForm.inputEndTime);
      periodEnd = new Date(
        `${format(new Date(tempEndDate), "yyyy-MM-dd")}T${format(
          new Date(tempEndTime),
          "HH:mm"
        )}`
      ).toISOString();
    }

    const bannerInput: UpdateProductLibBannerInput = {
      id: bannerId,
      brand: dataForm.inputBrand.value,
      name: dataForm.inputTextTitle,
      mimeType: imageUploadDescription?.type ? imageUploadDescription.type : "",
      path: partImg,
      description: dataForm.inputTextDescription
        .replace(/\n/g, "")
        .replace(/<p><br><\/p>/g, "<br>"),
      periodStart: periodStart,
      periodEnd: periodEnd,
      status: submitStatus,
      updatedBy: userInfo.email || userInfo.username,
    };

    const resUpdateProductLibBanner = await API.graphql(
      graphqlOperation(updateProductLibBanner, {
        input: { ...bannerInput },
      })
    );

    setTimeout(() => {
      setIsSubmit(false);
      if (resUpdateProductLibBanner) {
        if (submitStatus === bannerStatus.DRAFT) {
          setShowSaveDraft(true);
        }
        if (submitStatus === bannerStatus.PUBLISHED) {
          setShowSaveSubmit(true);
        }
      } else {
        console.log(
          "Error resUpdateProductLibBanner : ",
          resUpdateProductLibBanner
        );
      }
    }, 1000);
  };

  /* Need to have variable below for validate error */
  const onInvalid = (errors: any) => {
    setIsSubmit(false);
    console.error(errors);
  };

  const removeFileFunction = async () => {
    setIsLoading(true);
    try {
      if (imageUploadDescription.name) {
        // await Storage.remove(removeImage, { level: "public" });
        setIsLoading(false);
        setShowConfirmRemove(false);
        setImagePreview([]);
        setValue("inputPartImg", "");
      } else {
        setIsLoading(false);
        setShowConfirmRemove(false);
        setImagePreview([]);
        setValue("inputPartImg", "");
      }
    } catch (error) {
      console.log("Error ", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setTextEndDate(null);
    setTextEndTime(null);
  }, [textStartDate]);

  const fetchImages = async (key: string) => {
    /* Fetch data from resize folder */
    const { results } = await Storage.list(key, {
      level: "public",
      pageSize: 50,
    });

    const resultFilters = results.filter((coverKey) => coverKey.size! > 0);
    const signedUrl = resultFilters.map(async (image: any) => {
      const res = await Storage.get(image.key!, { level: "public" });
      return { key: image.key!, signedUrl: res };
    });
    const s3ImagesPromise = await Promise.allSettled(signedUrl);
    setImagePreview(s3ImagesPromise);
  };

  useEffect(() => {
    if (banner && banner.path) {
      fetchImages(`${prefixS3}/${banner.path}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="flex items-center gap-3 font-bold p-7">
        <FaHome
          className="cursor-pointer opacity-50"
          onClick={() => {
            navigate(`/`);
          }}
        />
        <span
          className="cursor-pointer opacity-50"
          onClick={() => {
            navigate(`/banners`);
          }}
        >
          Published
        </span>
        <span className="opacity-50">/</span>
        <span>Edit Banner</span>
      </div>
      <FormProvider {...methods}>
        <form
          className="w-full"
          onSubmit={handleSubmit(onSubmitForm, onInvalid)}
        >
          <div className="px-16 py-3">
            <h4 className="mb-7">Edit Banner</h4>
            <div className="grid grid-cols-3 gap-3 mb-24">
              <div className="col-span-2">
                <div
                  className={`border border-solid rounded-lg mb-5 p-5 drop-shadow-lg`}
                >
                  <div className="font-bold mb-2">Cover Banner</div>
                  <div className="opacity-50">
                    Cover Banner แสดงภาพที่ต้องการประชาสัมพันธ์ ส่วนนี้จะโชว์บน
                    Slide Banner และบนเนื้อหา
                  </div>
                  <div className="opacity-50 text-blue-700">
                    Supported formats: .png, .jpg ขนาด 680 x 380 pixel
                  </div>
                  <div className="mt-5">
                    {imagePreview.length > 0 ? (
                      <div className="amplify-storagemanager__file__list">
                        <div className="amplify-storagemanager__file">
                          <div className="amplify-storagemanager__file__wrapper">
                            <div className="amplify-storagemanager__file__image">
                              <img
                                className="amplify-image"
                                alt="ImageBanner"
                                src={`${imagePreview[0].value.signedUrl}`}
                              />
                            </div>
                            <div className="amplify-storagemanager__file__main">
                              <p className="amplify-text amplify-storagemanager__file__name">
                                {imageUploadDescription?.name
                                  ? imageUploadDescription.name
                                  : ""}
                              </p>
                              <p className="amplify-text amplify-storagemanager__file__size">
                                {imageUploadDescription?.size
                                  ? `${(
                                      imageUploadDescription.size / 1024
                                    ).toFixed(2)} KB`
                                  : ""}
                              </p>
                            </div>
                            <button
                              type="button"
                              className="mx-3"
                              onClick={() => {
                                setShowConfirmRemove(true);
                                setRemoveImage(imagePreview[0].value.key);
                              }}
                            >
                              <RiDeleteBin5Line className="text-red-500 w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <FileUploaderInput
                        pathPrefix={`${prefixS3}/${subfixS3}/${bannerId}/`}
                        name="inputPartImg"
                        setImagePreview={setImagePreview}
                        setImageUploadDescription={setImageUploadDescription}
                        setValue={setValue}
                      />
                    )}
                  </div>
                  {errors.inputPartImg ? (
                    <div className="mt-2">
                      <small className="font-light text-red-500">
                        {errors.inputPartImg.message}
                      </small>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
                <div
                  className={`border border-solid rounded-lg mb-5 p-5 drop-shadow-lg`}
                >
                  <div className="font-bold mb-2">Topic Or Campaign Title</div>
                  <div className="opacity-50 mb-5">
                    เพิ่ม "ชื่อหัวข้อ" หรือ "ชื่อแคมเปญ"
                    และรายละเอียดเนื้อหาเพื่อทำการประชาสัมพันธ์
                  </div>
                  <div className="mb-5">
                    <div className="font-bold mb-2">
                      Brand <span className="text-red-500">*</span>
                      {errors.inputBrand ? (
                        <small className="font-light text-red-500 ml-2">
                          {errors.inputBrand.message}
                        </small>
                      ) : (
                        ""
                      )}
                    </div>

                    <Controller
                      name="inputBrand"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          className="basic-single font-normal text-sm z-10"
                          classNamePrefix="select"
                          placeholder="เลือก brand"
                          isSearchable={true}
                          isClearable={false}
                          options={[brandTypeNone, ...brandOptions] as any}
                        />
                      )}
                    />
                  </div>
                  <div className="mb-5">
                    <div className="font-bold mb-2">
                      Title <span className="text-red-500">*</span>
                      {errors.inputTextTitle ? (
                        <small className="font-light text-red-500 ml-2">
                          {errors.inputTextTitle.message}
                        </small>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="absolute text-sm text-gray-700 opacity-50 right-8 mt-3">
                      {textTitle.length}/{titleMaxLength}
                    </div>
                    <textarea
                      {...register("inputTextTitle")}
                      id="message"
                      rows={4}
                      className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 pr-20 h-16 disabled:opacity-50"
                      placeholder="ใส่คำบรรยายไม่เกิน 90 ตัวอักษร"
                      maxLength={titleMaxLength}
                      onChange={(e) => setTextTitle(e.target.value)}
                    ></textarea>
                  </div>
                  <div>
                    <div className="font-bold mb-2">
                      Descriptions <span className="text-red-500">*</span>
                      {errors.inputTextDescription ? (
                        <small className="font-light text-red-500 ml-2">
                          {errors.inputTextDescription.message}
                        </small>
                      ) : (
                        ""
                      )}
                    </div>
                    <RichText
                      name="inputTextDescription"
                      setValue={setValue}
                      setTextDescription={setTextDescription}
                      value={textDescription.replace(/<br>/g, "<p><br></p>")}
                    />
                  </div>
                </div>
                <div
                  className={`border border-solid rounded-lg mb-5 p-5 drop-shadow-lg`}
                >
                  <div className="font-bold mb-2">Date & Time</div>
                  <div className="opacity-50">
                    ตั้งค่าวันและเวลาที่ต้องการประชาสัมพันธ์
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-5">
                    <div className="grid grid-row-2 gap-1">
                      <div className="font-bold">
                        วันเริ่มต้น <span className="text-red-600">*</span>
                        {errors.inputStartDate ? (
                          <small className="font-light text-red-500 ml-2">
                            {errors.inputStartDate.message}
                          </small>
                        ) : (
                          ""
                        )}
                      </div>
                      <div>
                        <div className="relative">
                          <Controller
                            control={control}
                            name="inputStartDate"
                            render={({ field }) => (
                              <DatePicker
                                {...field}
                                autoComplete="off"
                                className="w-full p-2 rounded-lg"
                                popperPlacement="top-end"
                                dateFormat="dd/MM/yyyy"
                                showPopperArrow={false}
                                selectsStart
                                // startDate={textStartDate}
                                // endDate={textEndDate}
                                minDate={new Date()}
                                placeholderText="กรอกวันเริ่มต้น"
                                selected={textStartDate}
                                onChange={(startDate) => {
                                  if (startDate) {
                                    field.onChange(startDate);
                                    setTextStartDate(startDate);
                                  } else {
                                    field.onChange(
                                      new Date(
                                        banner?.periodStart
                                          ? banner?.periodStart
                                          : ""
                                      )
                                    );
                                    setTextStartDate(
                                      new Date(
                                        banner?.periodStart
                                          ? banner?.periodStart
                                          : ""
                                      )
                                    );
                                  }
                                }}
                              />
                            )}
                          />
                          <FaRegCalendar className="absolute right-5 top-2 text-gray-500" />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-row-2 gap-1">
                      <div className="font-bold">
                        เวลาเริ่มต้น <span className="text-red-600">*</span>
                        {errors.inputStartTime ? (
                          <small className="font-light text-red-500 ml-2">
                            {errors.inputStartTime.message}
                          </small>
                        ) : (
                          ""
                        )}
                      </div>
                      <div>
                        <div className="relative">
                          <Controller
                            control={control}
                            name="inputStartTime"
                            render={({ field }) => (
                              <DatePicker
                                {...field}
                                autoComplete="off"
                                className="w-full p-2 rounded-lg"
                                popperPlacement="top-end"
                                dateFormat="HH:mm"
                                timeFormat="HH:mm"
                                showPopperArrow={false}
                                selected={textStartTime}
                                placeholderText="กรอกเวลาเริ่มต้น"
                                onChange={(startTime) => {
                                  if (startTime) {
                                    field.onChange(startTime);
                                    setTextStartTime(startTime);
                                  }
                                }}
                                showTimeSelect
                                showTimeSelectOnly
                                timeCaption="Time"
                              />
                            )}
                          />
                          <FaRegClock className="absolute right-5 top-3 text-gray-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-red-500 my-5">
                    หากต้องการให้ Banner มีการ publish ตลอดไป
                    ไม่ต้องใส่วันและเวลาสิ้นสุด
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-5">
                    <div className="grid grid-row-2 gap-1">
                      <div className="font-bold">
                        วันสิ้นสุด
                        {errors.inputEndDate ? (
                          <small className="font-light text-red-500 ml-2">
                            {errors.inputEndDate.message}
                          </small>
                        ) : (
                          ""
                        )}
                      </div>
                      <div>
                        <div className="relative">
                          <Controller
                            control={control}
                            name="inputEndDate"
                            render={({ field }) => (
                              <DatePicker
                                {...field}
                                autoComplete="off"
                                className="w-full p-2 rounded-lg"
                                popperPlacement="top-end"
                                dateFormat="dd/MM/yyyy"
                                showPopperArrow={false}
                                selectsEnd
                                // startDate={textStartDate}
                                // endDate={textEndDate}
                                minDate={
                                  // +1 day from start date
                                  textStartDate
                                    ? new Date(
                                        textStartDate.getTime() +
                                          24 * 60 * 60 * 1000
                                      )
                                    : null
                                }
                                placeholderText="กรอกวันสิ้นสุด"
                                selected={textEndDate}
                                onChange={(endDate) => {
                                  field.onChange(endDate);
                                  setTextEndDate(endDate);
                                  // clear end time when clear end date
                                  if (!endDate) {
                                    setValue("inputEndTime", "");
                                    setTextEndTime(null);
                                  }
                                  // set default end time when set end date
                                  if (!textEndTime) {
                                    setValue(
                                      "inputEndTime",
                                      endDate
                                        ? format(new Date(endDate), "HH:mm")
                                        : ""
                                    );
                                    setTextEndTime(endDate);
                                  }
                                }}
                                disabled={textStartDate === null ? true : false}
                              />
                            )}
                          />
                          <FaRegCalendar className="absolute right-5 top-2 text-gray-500" />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-row-2 gap-1">
                      <div className="font-bold">
                        เวลาสิ้นสุด
                        {errors.inputEndTime ? (
                          <small className="font-light text-red-500 ml-2">
                            {errors.inputEndTime.message}
                          </small>
                        ) : (
                          ""
                        )}
                      </div>
                      <div className="relative">
                        <Controller
                          control={control}
                          name="inputEndTime"
                          render={({ field }) => (
                            <DatePicker
                              {...field}
                              autoComplete="off"
                              className="w-full p-2 rounded-lg"
                              popperPlacement="top-end"
                              dateFormat="HH:mm"
                              timeFormat="HH:mm"
                              showPopperArrow={false}
                              selected={textEndTime}
                              placeholderText="กรอกเวลาสิ้นสุด"
                              onChange={(endTime) => {
                                field.onChange(endTime);
                                setTextEndTime(endTime);
                              }}
                              showTimeSelect
                              showTimeSelectOnly
                              timeCaption="Time"
                              disabled={textEndDate === null ? true : false}
                            />
                          )}
                        />
                        <FaRegClock className="absolute right-5 top-3 text-gray-500" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-[25.3rem]">
                <div
                  className={`border border-solid rounded-lg mb-5 p-10 drop-shadow-lg`}
                >
                  <div className="font-bold">Preview On Mobile Application</div>
                  <div className="mt-5">
                    {imagePreview.length > 0 ? (
                      <div>
                        <img
                          src={`${imagePreview[0].value.signedUrl}`}
                          alt={"ImageBanner"}
                          className="object-cover rounded-lg w-full h-full"
                        />
                      </div>
                    ) : (
                      <div className="opacity-30">
                        <img
                          src={"/no_img.jpg"}
                          alt={"ImageBanner"}
                          className="object-cover rounded-lg w-full h-full"
                        />
                      </div>
                    )}
                  </div>
                  {textTitle ? (
                    <h5 className="font-bold mt-5 break-words whitespace-pre-line">
                      {textTitle}
                    </h5>
                  ) : (
                    <div className="mt-5 opacity-30">
                      <div className="w-52 bg-gray-500">&nbsp;</div>
                      <div className="w-80 bg-gray-500 mt-1">&nbsp;</div>
                      <div className="w-72 bg-gray-500 mt-1">&nbsp;</div>
                    </div>
                  )}
                  {textDescription && textDescription !== "<p><br></p>" ? (
                    <div
                      className="mt-5 break-words"
                      dangerouslySetInnerHTML={{ __html: textDescription }}
                    ></div>
                  ) : (
                    <div className="mt-5 opacity-30">
                      <div className="w-52 bg-gray-500">&nbsp;</div>
                      <div className="w-80 bg-gray-500 mt-1">&nbsp;</div>
                      <div className="w-72 bg-gray-500 mt-1">&nbsp;</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="fixed bottom-0 left-0 w-full bg-white shadow-lg">
              <hr className="my-5" />
              <div className="mb-5 px-16">
                <div className="flex justify-between">
                  <div>
                    <button
                      className="border rounded-lg px-3 py-2 m-1 w-36"
                      disabled={isSubmit || isUpload}
                      type="button"
                      onClick={() => {
                        setShowConfirmCancel(true);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="flex gap-3 mr-12">
                    {submitStatus === bannerStatus.DRAFT && (
                      <button
                        className="flex items-center justify-center gap-2 border rounded-lg px-3 py-2 m-1 w-36 text-sky-700 border-sky-700 disabled:opacity-30"
                        disabled={isSubmit || isUpload}
                        type="submit"
                        onClick={() => {
                          setSubmitStatus(bannerStatus.DRAFT);
                        }}
                      >
                        <HiOutlineFolderDownload className="text-2xl" />
                        Save draft
                      </button>
                    )}
                    <button
                      className="flex items-center justify-center border rounded-lg px-3 py-2 m-1 w-36 bg-sky-700 text-white disabled:opacity-30"
                      disabled={isSubmit || isUpload}
                      type="submit"
                      onClick={() => {
                        setSubmitStatus(bannerStatus.PUBLISHED);
                      }}
                    >
                      {isSubmit ? (
                        <AiOutlineLoading3Quarters className="animate-spin h-5 w-5 mr-3 " />
                      ) : null}
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
      <ModalConfirm
        show={showConfirmRemove}
        onHide={() => {}}
        title="Remove image Confirmation !!"
        cancelText={"ยกเลิก"}
        cancelAction={() => setShowConfirmRemove(false)}
        okText={"ตกลง"}
        loading={isLoading}
        okAction={() => {
          setIsLoading(true);
          removeFileFunction();
        }}
        okClass="justify-center px-7 py-2 text-md font-medium text-white border border-red-700 rounded bg-red-600 hover:bg-red-700"
        maxWidth="max-w-md"
      >
        <>
          <div className="flex flex-col items-center justify-center mb-2 space-y-2 text-center">
            <RiDeleteBin5Line
              className="m-3"
              size={36}
              color="rgb(225 29 72)"
            />
            <h4>ต้องการลบ รูปภาพ?</h4>
            <h6>กด "ตกลง" เพื่อทำการลบ รูปภาพ</h6>
          </div>
        </>
      </ModalConfirm>
      <ModalConfirm
        show={showSaveSubmit}
        onHide={() => {}}
        cancelAction={() => setShowSaveSubmit(false)}
        okText={"ตกลง"}
        loading={isLoading}
        okAction={() => {
          setIsLoading(true);
          navigate(`/banners`);
        }}
        okClass="justify-center px-7 py-2 text-md font-medium text-white border border-sky-800 rounded bg-sky-700 hover:bg-sky-800"
        maxWidth="max-w-md"
      >
        <>
          <div className="flex flex-col items-center justify-center mb-2 space-y-2 text-center">
            <FaRegCheckCircle className="m-3 text-green-600" size={36} />
            <h4>บันทึกข้อมูล</h4>
            <h6>ระบบได้ทำการบันทึกข้อมูลเรียบร้อย</h6>
          </div>
        </>
      </ModalConfirm>
      <ModalConfirm
        show={showSaveDraft}
        onHide={() => {}}
        cancelAction={() => setShowSaveDraft(false)}
        okText={"ตกลง"}
        loading={isLoading}
        okAction={() => {
          setIsLoading(true);
          navigate(`/banners`);
        }}
        okClass="justify-center px-7 py-2 text-md font-medium text-white border border-sky-800 rounded bg-sky-700 hover:bg-sky-800"
        maxWidth="max-w-md"
      >
        <>
          <div className="flex flex-col items-center justify-center mb-2 space-y-2 text-center">
            <FaRegCheckCircle className="m-3 text-green-600" size={36} />
            <h4>บันทึกข้อมูล Draft</h4>
            <h6>ระบบได้ทำการบันทึกข้อมูล Draft เรียบร้อย</h6>
          </div>
        </>
      </ModalConfirm>
      <ModalConfirm
        show={showConfirmCancel}
        onHide={() => {}}
        cancelText={"ยกเลิก"}
        cancelAction={() => setShowConfirmCancel(false)}
        okText={"ตกลง"}
        loading={isLoading}
        okAction={() => {
          setIsLoading(true);
          navigate(`/banners`);
        }}
        okClass="justify-center px-7 py-2 text-md font-medium text-white border border-red-800 rounded bg-red-700 hover:bg-red-800"
        maxWidth="max-w-md"
      >
        <>
          <div className="flex flex-col items-center justify-center mb-2 space-y-2 text-center">
            <h4>ต้องการออกจากหน้านี้หรือไม่?</h4>
            <h6>กด "ตกลง" เพื่อต้องการออกจากหน้านี้</h6>
          </div>
        </>
      </ModalConfirm>
    </div>
  );
}
export default BannerUpdate;
